import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Lock } from 'lucide-react';
import { popIn } from './primitives';

const SHEET_WIDTH = 316;
const SHEET_GAP_PX = 6;
const VIEWPORT_PAD_PX = 12;
const ICON_PROPS = { size: 16, strokeWidth: 1.8 };

export { ICON_PROPS };

const KebabBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  cursor: pointer;
  border: 1px solid ${({ $open }) => ($open ? '#d3ddd4' : 'transparent')};
  background: ${({ $open }) => ($open ? '#f4f7f4' : 'transparent')};
  color: #5a655c;
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  padding: 0;

  &:hover {
    border-color: #d3ddd4;
    background: #f4f7f4;
  }
`;

const SheetRoot = styled.div`
  position: fixed;
  z-index: 1200;
  width: ${SHEET_WIDTH}px;
  max-width: calc(100vw - ${VIEWPORT_PAD_PX * 2}px);
  background: #fff;
  border: 1px solid #e6ece6;
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(20, 40, 28, 0.16);
  overflow: hidden;
  font-family: ${({ theme }) => theme.fontBody};
  animation: ${popIn} 0.15s cubic-bezier(0.2, 0.7, 0.3, 1);
  max-height: min(70vh, calc(100vh - ${VIEWPORT_PAD_PX * 2}px));
  overflow-y: auto;
`;

const SheetHeader = styled.div`
  padding: 14px 16px 11px;
  border-bottom: 1px solid #f0f3f0;
`;

const SheetEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #9aa69c;
`;

const SheetTitle = styled.div`
  margin-top: 2px;
  font-size: 14px;
  font-weight: 700;
  color: #1a241d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SheetBody = styled.div`
  padding: 12px;
`;

const PrimaryBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 13px;
  border-radius: 11px;
  background: ${({ $hover }) => ($hover ? '#e1f1e7' : '#eaf6ee')};
  color: #1f7a4d;
  cursor: pointer;
  margin-bottom: 12px;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s;

  svg {
    flex: none;
    color: #2f9d63;
  }

  span {
    font-size: 14.5px;
    font-weight: 700;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const GroupLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #9aa69c;
  padding: 2px 4px 7px;
`;

const GroupBox = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 11px;
  padding: 4px;
  margin-bottom: 12px;
`;

const RowBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 11px;
  border-radius: 8px;
  font-size: 14px;
  color: ${({ $danger }) => ($danger ? '#c0392b' : '#1f2a22')};
  cursor: ${({ $locked, disabled }) => ($locked ? 'pointer' : disabled ? 'not-allowed' : 'pointer')};
  border: none;
  background: ${({ $hover, $danger, $locked, theme }) => {
    if ($locked) return $hover ? theme.accentSoft : 'transparent';
    if (!$hover) return 'transparent';
    return $danger ? '#fbeceb' : theme.surface2;
  }};
  width: 100%;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s;

  svg {
    flex: none;
    color: ${({ $danger, theme }) => ($danger ? '#c0392b' : theme.textMuted)};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const RowLock = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.textSubtle};
`;

const SheetDivider = styled.div`
  height: 1px;
  background: #f0f3f0;
  margin: 7px 10px;
`;

function SheetRow({ icon, label, danger, disabled, locked, onClick }) {
  const [hover, setHover] = useState(false);
  const isHardDisabled = disabled && !locked;

  return (
    <RowBtn
      type="button"
      role="menuitem"
      aria-disabled={locked || disabled || undefined}
      $danger={danger}
      $hover={hover && !isHardDisabled}
      $locked={locked}
      disabled={isHardDisabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      {icon}
      {label}
      {locked ? (
        <RowLock aria-hidden="true">
          <Lock size={14} strokeWidth={2} />
        </RowLock>
      ) : null}
    </RowBtn>
  );
}

function computeSheetCoords(triggerEl, sheetEl) {
  const triggerRect = triggerEl.getBoundingClientRect();
  const sheetHeight = sheetEl.offsetHeight;
  const flipUp = triggerRect.bottom + sheetHeight + SHEET_GAP_PX + VIEWPORT_PAD_PX > window.innerHeight;
  const top = flipUp
    ? Math.max(VIEWPORT_PAD_PX, triggerRect.top - sheetHeight - SHEET_GAP_PX)
    : triggerRect.bottom + SHEET_GAP_PX;
  const right = Math.max(VIEWPORT_PAD_PX, window.innerWidth - triggerRect.right);
  return { top, right };
}

function ActionSheetPanel({
  sheetRef,
  coords,
  entityLabel,
  title,
  menuAriaLabel,
  primary,
  groupLabel,
  groupItems,
  items,
  dangerItem,
  onItemClick,
}) {
  const [primaryHover, setPrimaryHover] = useState(false);

  const renderItems = (list) => list.map((item) => (
    <SheetRow
      key={item.key}
      icon={item.icon}
      label={item.label}
      danger={item.danger}
      disabled={item.disabled}
      locked={item.locked}
      onClick={() => onItemClick(item)}
    />
  ));

  return (
    <SheetRoot
      ref={sheetRef}
      role="menu"
      aria-label={menuAriaLabel}
      style={{
        top: coords?.top ?? -9999,
        right: coords?.right ?? VIEWPORT_PAD_PX,
        visibility: coords ? 'visible' : 'hidden',
      }}
    >
      <SheetHeader>
        <SheetEyebrow>{entityLabel}</SheetEyebrow>
        <SheetTitle title={title}>{title}</SheetTitle>
      </SheetHeader>

      <SheetBody>
        {primary && (
          <PrimaryBtn
            type="button"
            role="menuitem"
            $hover={primaryHover}
            disabled={primary.disabled && !primary.locked}
            style={primary.locked ? { opacity: 0.62 } : undefined}
            onMouseEnter={() => setPrimaryHover(true)}
            onMouseLeave={() => setPrimaryHover(false)}
            onClick={() => onItemClick(primary)}
          >
            {primary.icon}
            <span>{primary.label}</span>
          </PrimaryBtn>
        )}

        {groupItems?.length > 0 && (
          <>
            {groupLabel && <GroupLabel>{groupLabel}</GroupLabel>}
            <GroupBox>{renderItems(groupItems)}</GroupBox>
          </>
        )}

        {items?.length > 0 && renderItems(items)}

        {dangerItem && (
          <>
            <SheetDivider aria-hidden="true" />
            <SheetRow
              icon={dangerItem.icon}
              label={dangerItem.label}
              danger
              locked={dangerItem.locked}
              disabled={dangerItem.disabled}
              onClick={() => onItemClick(dangerItem)}
            />
          </>
        )}
      </SheetBody>
    </SheetRoot>
  );
}

/**
 * Kebab trigger + grouped action sheet (Alt A) for DataTable row actions.
 * Presentation only — parent supplies item config and callbacks via onAction(key).
 */
export default function GroupedRowActionSheet({
  entityLabel,
  title,
  menuAriaLabel = 'Row actions',
  primary,
  groupLabel,
  groupItems = [],
  items = [],
  dangerItem,
  onAction,
  layoutKey = '',
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const sheetRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const reposition = useCallback(() => {
    if (!triggerRef.current || !sheetRef.current) return;
    setCoords(computeSheetCoords(triggerRef.current, sheetRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    reposition();
    return undefined;
  }, [open, reposition, title, layoutKey, primary?.label, groupItems.length, items.length]);

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (e) => {
      const target = e.target;
      if (triggerRef.current?.contains(target)) return;
      if (sheetRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  const handleItemClick = (item) => {
    if (item?.locked) {
      close();
      if (item.onBlockedClick) {
        item.onBlockedClick();
      }
      return;
    }
    if (item?.disabled) {
      return;
    }
    close();
    onAction?.(item.key);
  };

  const toggleOpen = (e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <>
      <KebabBtn
        ref={triggerRef}
        type="button"
        aria-label="Row actions"
        aria-expanded={open}
        aria-haspopup="menu"
        $open={open}
        onClick={toggleOpen}
      >
        ⋯
      </KebabBtn>

      {open && createPortal(
        <ActionSheetPanel
          sheetRef={sheetRef}
          coords={coords}
          entityLabel={entityLabel}
          title={title}
          menuAriaLabel={menuAriaLabel}
          primary={primary}
          groupLabel={groupLabel}
          groupItems={groupItems}
          items={items}
          dangerItem={dangerItem}
          onItemClick={handleItemClick}
        />,
        document.body,
      )}
    </>
  );
}
